(() => {
  const root = document.documentElement;
  const announcement = document.querySelector('.section-announcement-bar');
  const header = document.querySelector('.section-header');
  const countdown = document.getElementById('GlobalCountdown');

  if (!header) return;

  const DESKTOP_MIN_PX = 768;
  const TOP_REVEAL_PX = 8;
  const DOWN_INTENT_PX = 12;
  const UP_INTENT_PX = 4;
  const DELTA_TOLERANCE_PX = 2;
  const DETACH_SETTLE_MS = 500;
  const ACTIVATION_SETTLE_MS = 1200;
  const keepsHeaderVisible = document.body.classList.contains('template-suffix-measurement-new')
    || document.body.classList.contains('template-suffix-drapery-measurement-new');
  const productId = document.querySelector('.product-form[data-cz-pdp-master="drapery"]')?.dataset.productId || '';
  const primesNativeScroll = root.dataset.czDraperyTemplate === 'canonical-v1'
    && ['11719366345022', '12044121178430'].includes(productId);
  let lastY = Math.max(0, window.scrollY);
  let direction = 0;
  let directionTravel = 0;
  let frameRequested = false;
  let ignoreReverseUntil = 0;
  let ignoreLayoutScrollUntil = 0;
  let ignoreOptionLayoutUntil = 0;
  let holdOptionLayoutUntilInput = false;
  let protectedOptionRow = null;
  let protectedOptionRowMinHeight = '';
  let lastViewportWidth = window.innerWidth;
  let headerHeight = Math.ceil(header.getBoundingClientRect().height);
  let returnStartY = 0;
  let lastUpwardTravel = 0;

  const isDesktop = () => window.innerWidth >= DESKTOP_MIN_PX;

  const resetReturnFlow = () => {
    returnStartY = 0;
    lastUpwardTravel = 0;
    header.style.removeProperty('margin-bottom');
    root.classList.remove('cz-header-returning');
  };

  const updateReturnFlow = (currentY = Math.max(0, window.scrollY)) => {
    if (!root.classList.contains('cz-header-returning')) return;
    if (!isDesktop() || currentY <= TOP_REVEAL_PX || returnStartY <= 0) {
      resetReturnFlow();
      return;
    }
    const remaining = Math.min(1, currentY / returnStartY);
    header.style.setProperty('margin-bottom', `${-headerHeight * remaining}px`);
  };

  const measure = () => {
    headerHeight = Math.ceil(header.getBoundingClientRect().height);
    root.style.setProperty('--cz-header-full-height', `${headerHeight}px`);
  };

  const cartDrawerIsOpen = () => Boolean(
    document.querySelector('cart-drawer[open], cart-drawer[aria-hidden="false"]')
  );

  const hasInteractionLock = () => {
    const activeElement = document.activeElement;
    const headerHasFocus = activeElement && header.contains(activeElement);

    return Boolean(
      window.Shopify?.designMode ||
      headerHasFocus ||
      document.body.classList.contains('show-search') ||
      document.body.classList.contains('reveal-mobile-nav') ||
      header.classList.contains('section-header--nav-open') ||
      header.classList.contains('section-header--nav-hover') ||
      header.querySelector('[aria-expanded="true"]') ||
      cartDrawerIsOpen()
    );
  };

  const resetNavigationPaintGuard = () => {
    header.classList.remove('section-header--navigation-pending');
  };

  const closeHeaderMenusForNavigation = () => {
    header.classList.add('section-header--navigation-pending');
    header.classList.remove('section-header--nav-open', 'section-header--nav-hover');
    header.querySelectorAll('.navigation__item--show-children').forEach((item) => {
      item.classList.remove('navigation__item--show-children');
    });
    header.querySelectorAll('.navigation__tier-1 > .navigation__item--with-children > .navigation__link[aria-expanded="true"]').forEach((control) => {
      control.setAttribute('aria-expanded', 'false');
    });
    header.querySelectorAll('[data-cz-menu-video]').forEach((video) => {
      if (typeof video.pause === 'function') video.pause();
    });
  };

  const handleHeaderNavigation = (event) => {
    if (
      event?.defaultPrevented ||
      (typeof event?.button === 'number' && event.button !== 0) ||
      event?.metaKey ||
      event?.ctrlKey ||
      event?.shiftKey ||
      event?.altKey
    ) return;
    const link = event?.target?.closest?.(
      '.navigation__tier-1 > .navigation__item > .navigation__link[href]'
    );
    if (!link || !header.contains(link) || link.target === '_blank' || link.hasAttribute('download')) return;
    const href = link.getAttribute('href')?.trim();
    if (!href || href.startsWith('#')) return;
    closeHeaderMenusForNavigation();
  };

  const setHidden = (hidden, incomingDownward = false) => {
    if (keepsHeaderVisible) {
      resetReturnFlow();
      root.classList.remove('cz-header-detached', 'cz-header-hidden');
      root.dataset.czHeaderScrollState = isDesktop() ? 'visible-stable' : 'mobile-fixed';
      root.style.setProperty('--theme-sticky-header-height', `${headerHeight}px`);
      return;
    }
    const shouldHide = Boolean(
      hidden &&
      isDesktop() &&
      (incomingDownward || window.scrollY > TOP_REVEAL_PX) &&
      !hasInteractionLock()
    );
    if (shouldHide) {
      resetReturnFlow();
      if (!root.classList.contains('cz-header-detached')) {
        ignoreReverseUntil = Date.now() + DETACH_SETTLE_MS;
      }
      root.classList.add('cz-header-detached');
    } else if (!isDesktop() || window.scrollY <= TOP_REVEAL_PX) {
      resetReturnFlow();
      root.classList.remove('cz-header-detached');
    }
    root.classList.toggle('cz-header-hidden', shouldHide);
    root.dataset.czHeaderScrollState = isDesktop()
      ? (shouldHide ? 'hidden' : 'visible')
      : 'mobile-fixed';
    root.style.setProperty(
      '--theme-sticky-header-height',
      shouldHide ? '0px' : `${headerHeight}px`
    );
  };

  const releaseOptionRowGeometry = () => {
    if (!protectedOptionRow) return;
    if (protectedOptionRowMinHeight) {
      protectedOptionRow.style.setProperty('min-height', protectedOptionRowMinHeight);
    } else {
      protectedOptionRow.style.removeProperty('min-height');
    }
    protectedOptionRow = null;
    protectedOptionRowMinHeight = '';
  };

  const preserveOptionRowGeometry = (optionControl) => {
    const productRow = optionControl?.closest?.('product-form.js-product')
      || optionControl?.closest?.('.product-form')?.closest?.('product-form.js-product');
    const gallery = productRow?.querySelector?.(':scope > .media-gallery');
    const details = productRow?.querySelector?.(':scope > .product-column-right');
    const rowBox = productRow?.getBoundingClientRect?.();
    const galleryBox = gallery?.getBoundingClientRect?.();
    const detailsBox = details?.getBoundingClientRect?.();
    const twoColumn = rowBox && galleryBox && detailsBox
      && Math.abs(galleryBox.top - detailsBox.top) <= 2
      && detailsBox.left >= galleryBox.right - 2;
    if (!twoColumn || !Number.isFinite(rowBox.height) || rowBox.height <= 0) {
      releaseOptionRowGeometry();
      return;
    }
    if (protectedOptionRow !== productRow) {
      releaseOptionRowGeometry();
      protectedOptionRow = productRow;
      protectedOptionRowMinHeight = productRow.style.getPropertyValue('min-height');
    }
    const currentFloor = parseFloat(productRow.style.getPropertyValue('min-height')) || 0;
    productRow.style.setProperty('min-height', `${Math.max(currentFloor, Math.ceil(rowBox.height))}px`);
  };

  const releaseOptionLayoutProtection = () => {
    holdOptionLayoutUntilInput = false;
    ignoreOptionLayoutUntil = 0;
    releaseOptionRowGeometry();
  };

  const revealForInteraction = () => {
    direction = 0;
    directionTravel = 0;
    setHidden(false);
  };

  const settleReturnForActivation = () => {
    releaseOptionLayoutProtection();
    // Wait for click, rather than pointerdown/focusin, so removing the negative
    // margin cannot move the target between pointerdown and pointerup.
    const returning = root.classList.contains('cz-header-returning');
    if (!returning && Date.now() >= ignoreLayoutScrollUntil) return;
    ignoreLayoutScrollUntil = Date.now() + ACTIVATION_SETTLE_MS;
    if (returning) resetReturnFlow();
    revealForInteraction();
  };

  const preserveHeaderGeometryForOptionLayout = (event) => {
    const optionControl = primesNativeScroll && event?.target?.closest?.('.cz-config-step');
    if (!optionControl || !isDesktop()) return false;
    preserveOptionRowGeometry(optionControl);
    ignoreOptionLayoutUntil = Date.now() + ACTIVATION_SETTLE_MS;
    holdOptionLayoutUntilInput = true;
    direction = 0;
    directionTravel = 0;
    return true;
  };

  const updateFromScroll = () => {
    frameRequested = false;
    const currentY = Math.max(0, window.scrollY);
    const delta = currentY - lastY;

    if (!isDesktop()) {
      revealForInteraction();
      lastY = currentY;
      return;
    }

    if (holdOptionLayoutUntilInput || Date.now() < ignoreOptionLayoutUntil) {
      lastY = currentY;
      return;
    }

    if (currentY <= TOP_REVEAL_PX || hasInteractionLock()) {
      revealForInteraction();
      lastY = currentY;
      return;
    }

    updateReturnFlow(currentY);
    const returningToTop = root.classList.contains('cz-header-returning');

    if (Date.now() < ignoreLayoutScrollUntil) {
      revealForInteraction();
      lastY = currentY;
      return;
    }

    lastY = currentY;
    if (Math.abs(delta) <= DELTA_TOLERANCE_PX) return;
    if (returningToTop) {
      if (delta < 0) lastUpwardTravel = -delta;
      direction = -1;
      directionTravel = 0;
      setHidden(false);
      return;
    }

    const nextDirection = delta > 0 ? 1 : -1;
    if (
      nextDirection < 0 &&
      root.classList.contains('cz-header-hidden') &&
      Date.now() < ignoreReverseUntil
    ) {
      direction = 0;
      directionTravel = 0;
      return;
    }
    if (direction !== nextDirection) {
      direction = nextDirection;
      directionTravel = 0;
    }
    directionTravel += Math.abs(delta);

    if (direction > 0 && directionTravel >= DOWN_INTENT_PX) {
      setHidden(true);
      directionTravel = 0;
    } else if (direction < 0 && directionTravel >= UP_INTENT_PX) {
      setHidden(false);
      directionTravel = 0;
    }
  };

  const requestScrollUpdate = () => {
    if (frameRequested) return;
    frameRequested = true;
    window.requestAnimationFrame(updateFromScroll);
  };

  const primeDownwardScroll = (event) => {
    const target = event?.target;
    if (
      event?.defaultPrevented ||
      target?.closest?.('input, textarea, select, [contenteditable="true"]')
    ) return;
    releaseOptionLayoutProtection();
    ignoreLayoutScrollUntil = 0;
    const downwardStateIsApplied = isDesktop()
      && root.classList.contains('cz-header-detached')
      && root.classList.contains('cz-header-hidden')
      && !root.classList.contains('cz-header-returning')
      && root.dataset.czHeaderScrollState === 'hidden'
      && !hasInteractionLock();
    if (downwardStateIsApplied) return;
    setHidden(true, true);
  };

  const beginTopReturn = (event) => {
    const target = event?.target;
    if (
      event?.defaultPrevented ||
      !isDesktop() ||
      target?.closest?.('input, textarea, select, [contenteditable="true"]')
    ) return;
    releaseOptionLayoutProtection();
    ignoreLayoutScrollUntil = 0;
    if (root.classList.contains('cz-header-returning')) {
      if (lastUpwardTravel > 0 && window.scrollY <= lastUpwardTravel) {
        resetReturnFlow();
      }
      return;
    }
    if (!root.classList.contains('cz-header-detached')) return;
    returnStartY = Math.max(1, window.scrollY);
    header.style.setProperty('margin-bottom', `${-headerHeight}px`);
    root.classList.add('cz-header-returning');
    root.classList.remove('cz-header-detached');
    ignoreReverseUntil = 0;
  };

  const prepareImmediateTopReturn = (event) => {
    const target = event?.target;
    if (
      event?.defaultPrevented ||
      !isDesktop() ||
      target?.closest?.('input, textarea, select, [contenteditable="true"]')
    ) return;
    releaseOptionLayoutProtection();
    resetReturnFlow();
    root.classList.remove('cz-header-detached', 'cz-header-hidden');
    root.dataset.czHeaderScrollState = 'visible';
    root.style.setProperty('--theme-sticky-header-height', `${headerHeight}px`);
    ignoreReverseUntil = 0;
    ignoreLayoutScrollUntil = 0;
  };

  const settlesLayoutOnActivation = (event) => Boolean(
    !event?.defaultPrevented &&
    event?.target?.closest?.('button, a, summary, [role="button"], [role="radio"], [role="checkbox"]')
  );

  const handleNavigationKey = (event) => {
    const key = event?.key;
    if ((key === 'Enter' || key === ' ') && settlesLayoutOnActivation(event)) {
      if (!preserveHeaderGeometryForOptionLayout(event)) settleReturnForActivation();
      return;
    }
    if (key === 'Home') {
      prepareImmediateTopReturn(event);
      return;
    }
    const movesDown = key === 'ArrowDown' || key === 'PageDown' || key === 'End'
      || (key === ' ' && !event.shiftKey);
    const movesUp = key === 'ArrowUp' || key === 'PageUp'
      || (key === ' ' && event.shiftKey);
    if (movesDown) primeDownwardScroll(event);
    else if (movesUp) beginTopReturn(event);
  };

  const handlePointerDown = (event) => {
    if (!event?.target?.closest?.('.cz-config-step')) releaseOptionLayoutProtection();
    if (
      event?.button === 0 &&
      event.clientX >= document.documentElement.clientWidth
    ) {
      if (root.classList.contains('cz-header-detached')) beginTopReturn(event);
      else primeDownwardScroll(event);
    }
  };

  const syncLayout = () => {
    measure();
    if (holdOptionLayoutUntilInput || Date.now() < ignoreOptionLayoutUntil) return;
    if (!isDesktop()) {
      revealForInteraction();
    } else if (hasInteractionLock()) {
      if (
        root.classList.contains('cz-header-hidden') &&
        Date.now() < ignoreReverseUntil
      ) return;
      revealForInteraction();
    } else {
      setHidden(root.classList.contains('cz-header-hidden'));
    }
  };

  const interactionObserver = new MutationObserver(syncLayout);
  interactionObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class']
  });
  interactionObserver.observe(header, {
    attributes: true,
    attributeFilter: ['class']
  });
  header.querySelectorAll('[aria-expanded]').forEach((control) => {
    interactionObserver.observe(control, {
      attributes: true,
      attributeFilter: ['aria-expanded']
    });
  });
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    interactionObserver.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['open', 'aria-hidden']
    });
  }

  const resizeObserver = new ResizeObserver(syncLayout);
  [announcement, header, countdown].filter(Boolean).forEach((element) => resizeObserver.observe(element));

  header.addEventListener('click', handleHeaderNavigation, { capture: true });
  window.addEventListener('scroll', requestScrollUpdate, { passive: true });
  window.addEventListener('pagehide', closeHeaderMenusForNavigation);
  if (primesNativeScroll) {
    window.addEventListener('wheel', (event) => {
      if (event.deltaY > 0) primeDownwardScroll(event);
      else if (event.deltaY < 0) beginTopReturn(event);
    }, { passive: true, capture: true });
    window.addEventListener('touchmove', releaseOptionLayoutProtection, { passive: true, capture: true });
    document.addEventListener('keydown', handleNavigationKey, { capture: true });
    document.addEventListener('pointerdown', handlePointerDown, { passive: true, capture: true });
    document.addEventListener('click', (event) => {
      if (settlesLayoutOnActivation(event) && !preserveHeaderGeometryForOptionLayout(event)) settleReturnForActivation();
    }, { capture: true });
  }
  window.addEventListener('resize', () => {
    const widthChanged = window.innerWidth !== lastViewportWidth;
    lastViewportWidth = window.innerWidth;
    lastY = Math.max(0, window.scrollY);
    if (widthChanged) {
      releaseOptionLayoutProtection();
      revealForInteraction();
    }
    syncLayout();
  }, { passive: true });
  window.addEventListener('pageshow', () => {
    resetNavigationPaintGuard();
    releaseOptionLayoutProtection();
    lastY = Math.max(0, window.scrollY);
    revealForInteraction();
    syncLayout();
  });
  document.addEventListener('focusin', (event) => {
    const isMeasurementToolFocus = Boolean(
      document.body.classList.contains('template-suffix-drapery-measurement-dev') &&
      event?.target?.matches?.('cozyology-measurement-tool')
    );
    const isDraperyOptionFocus = Boolean(
      primesNativeScroll && event?.target?.closest?.('.cz-config-step')
    );
    // Accordion focus must not reveal a detached Header. Its height variable is
    // also the sticky gallery offset, so revealing it while the accordion keeps
    // its clicked row anchored visibly moves the left image. Header/menu focus
    // and every non-option interaction retain the existing reveal behavior.
    if (!isMeasurementToolFocus && !isDraperyOptionFocus) {
      releaseOptionLayoutProtection();
      revealForInteraction();
    }
  });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      releaseOptionLayoutProtection();
      revealForInteraction();
    }
  });

  measure();
  root.classList.add('cz-header-scroll-ready');
  setHidden(false);
})();
